import { parseServerTime } from "@/filters/DateAndTime";
import GraphRanges from "@/mixins/GraphRanges";
import { Patient } from "@/store/modules/patient/types";
import _ from "lodash";
import { mixins } from "vue-class-component";
import { Component } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { MultiChartConfig } from "../shared/chart/MultiChartWithSharedRange";

const patientNamespace = { namespace: "patient" };
const navigationNamespace = { namespace: "navigation" };
@Component({})
export default class WeightAndIoBalance extends mixins(GraphRanges) {
  @Getter("graph", patientNamespace)
  graph!: any;

  @Getter("current", patientNamespace)
  current!: Patient;

  @Getter("darkMode", navigationNamespace)
  darkMode!: boolean;

  mounted() {
    this.times = [{ text: "7 days", hours: 7 * 24 }];
    this.selectedTime = 7 * 24;
  }

  get charts(): MultiChartConfig[] {
    const weight = {
      title: "Weight",
      data: this.graph(["Weight"]),
      library: {},
      suggestedMin: this.admitWeightValue - 10,
      suggestedMax: this.admitWeightValue + 10
    };

    const ioBalance = {
      title: "I/O Balance",
      data: this.ioCombinedPerDay,
      type: "bar",
      suggestedMin: -800,
      suggestedMax: 800,
      library: {
        legend: {
          labels: {
            usePointStyle: true
          },
          display: false
        },
        scales: {
          yAxes: [
            {
              stacked: true,
              gridLines: {
                zeroLineColor: "#fffff",
                zeroLineWidth: 3
              }
            }
          ],
          xAxes: [
            {
              maxBarThickness: 42,
              minBarThickness: 6,
              stacked: true
            }
          ]
        }
      }
    };
    return [weight, ioBalance];
  }

  get admitWeightValue() {
    const admitWeight = _.get(
      this.current,
      "body.current.AdmitWeight.value",
      null
    );
    return admitWeight;
  }

  get admitWeight() {
    if (this.admitWeightValue) {
      return Math.round(this.admitWeightValue) + " kg";
    } else {
      return "";
    }
  }

  get ioCombinedPerDay() {
    const graphData = this.graph(["IO"]);
    if (!graphData.length || !graphData[0] || !graphData[0].data) {
      return [];
    }
    const totalPerDay: Map<string, { date: string; value: number }> = new Map();

    graphData[0].data.forEach((p: { x: string; y: number }) => {
      const date = parseServerTime(p.x);
      const key = date.format("YYYY-MM-DD");
      if (!totalPerDay.has(key)) {
        totalPerDay.set(key, {
          date: key,
          value: 0
        });
      }
      totalPerDay.get(key)!.value += p.y;
    });

    const positiveData: Array<{ x: string; y: number }> = [];
    const negativeData: Array<{ x: string; y: number }> = [];
    const splitGraphData = [
      {
        label: "IO",
        data: positiveData,
        datasets: [{ barThickness: "flex" }],
        borderColor: "#00cc00",
        borderWidth: 3,
        backgroundColor: "rgb(0,204,0,0.64)"
      },
      {
        label: "IO",
        data: negativeData,
        datasets: [{ barThickness: "flex" }],
        borderColor: this.darkMode ? "#7cb6bd" : "#498991",
        borderWidth: 3,
        backgroundColor: this.darkMode
          ? "rgba(124, 182, 189, 0.64)"
          : "rgba(73, 137, 145, 0.64)"
      }
    ];
    totalPerDay.forEach(({ date, value }) => {
      if (value > 0) {
        splitGraphData[0].data.push({
          x: date,
          y: value
        });
      } else {
        splitGraphData[1].data.push({
          x: date,
          y: value
        });
      }
    });
    return splitGraphData;
  }

  get currentWeight() {
    return _.get(this.current, "body.atoms.weight.legend", "");
  }

  get idealWeight() {
    return _.get(this.current, "body.atoms.weightIdeal.value", "") + " kg";
  }

  get currentHeight() {
    return _.get(this.current, "body.atoms.height.legend", "");
  }

  get BMI() {
    return _.get(this.current, "body.atoms.bmi.value", "");
  }
}
